// Advisor "My originations" — advisor-originated deals (curated_deals).
// PRD: .local/prds/curated-deals-v1.md
//
// Single-page surface with three modes:
//   list        — all my deals filtered by status
//   compose     — new draft / edit existing draft
//   detail      — distributions, earnings, activity for one deal

import { useEffect, useMemo, useState } from "react";
import { Plus, Send, Loader2, AlertCircle, ChevronLeft, FileText, Users, TrendingUp, IndianRupee, Eye, Heart, Calendar, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import HelpTip from "@/components/help-tip";

const BASE = import.meta.env.BASE_URL;
const tok = () => localStorage.getItem("banker_token") || "";
const headers = () => ({ "Content-Type": "application/json", "x-banker-token": tok() });

const inr = (n: number | null | undefined) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;
const inrShort = (n: number | null | undefined) => {
  if (n == null) return "—";
  const x = Number(n);
  if (x >= 10000000) return `₹${(x / 10000000).toFixed(1)}Cr`;
  if (x >= 100000) return `₹${(x / 100000).toFixed(1)}L`;
  return `₹${x.toLocaleString("en-IN")}`;
};

const STATUS_TONE: Record<string, string> = {
  draft: "bg-white/[0.06] text-white/70 border-white/15",
  submitted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  admin_review: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  changes_requested: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  live: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  paused: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  closed: "bg-white/[0.06] text-white/40 border-white/10",
};

export default function BankerOriginations() {
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [view, setView] = useState<"list" | "compose" | "detail">("list");
  const [editingDeal, setEditingDeal] = useState<any | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const load = async () => {
    setError(null);
    try {
      const r = await fetch(`${BASE}api/banker/curated-deals`, { headers: headers() });
      if (r.status === 403) {
        const e = await r.json();
        setError(e.error || "You don't have origination permission yet.");
        setDeals([]);
      } else if (r.ok) {
        setDeals(await r.json());
      } else {
        setError(`Failed to load (${r.status}).`);
      }
    } catch { setError("Network error."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => filter === "all" ? deals : deals.filter(d => d.status === filter), [deals, filter]);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const d of deals) c[d.status] = (c[d.status] || 0) + 1;
    return c;
  }, [deals]);

  if (view === "compose") {
    return <DealComposer existing={editingDeal} onCancel={() => { setView("list"); setEditingDeal(null); }} onSaved={async () => { await load(); setView("list"); setEditingDeal(null); }} />;
  }
  if (view === "detail" && detailId != null) {
    return <DealDetail dealId={detailId} onBack={() => { setView("list"); setDetailId(null); load(); }} onEdit={(d) => { setEditingDeal(d); setView("compose"); }} />;
  }

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-[#FF5C00]/30 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-emerald-300" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">My originations</h1>
            <p className="text-white/55 text-sm mt-1">Curated deals you've sourced. Submit a draft, admin approves it, and the system distributes to your investors with all six agents activated.</p>
          </div>
        </div>
        <button onClick={() => { setEditingDeal(null); setView("compose"); }} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> New origination
        </button>
      </div>

      {error && <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200 flex items-start gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span></div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { k: "all", label: "All", count: deals.length },
          { k: "draft", label: "Drafts", count: counts.draft || 0 },
          { k: "submitted", label: "In review", count: (counts.submitted || 0) + (counts.admin_review || 0) + (counts.changes_requested || 0) },
          { k: "live", label: "Live", count: counts.live || 0 },
          { k: "closed", label: "Closed", count: (counts.closed || 0) + (counts.rejected || 0) },
        ].map(t => (
          <button key={t.k} onClick={() => setFilter(t.k)} className={`text-left rounded-xl border p-3.5 transition-all ${filter === t.k ? "ring-2 ring-[#FF5C00]/40 border-[#FF5C00]/40" : "border-white/[0.08] hover:border-white/[0.2]"} bg-white/[0.04]`}>
            <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{t.label}</p>
            <p className="text-2xl font-bold text-white mt-1">{t.count}</p>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white/[0.04] rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-10 text-center">
            <FileText className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="text-white/55 text-sm font-medium">{deals.length === 0 ? "No originations yet." : "Nothing in this filter."}</p>
            <p className="text-white/40 text-xs mt-1">{deals.length === 0 ? "Click 'New origination' to draft your first one." : "Try another filter."}</p>
          </div>
        ) : (
          filtered.map(d => (
            <button key={d.id} onClick={() => { setDetailId(d.id); setView("detail"); }} className="w-full text-left bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.18] rounded-xl p-4 transition-all">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-semibold truncate">{d.title}</h3>
                    <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${STATUS_TONE[d.status] || "bg-white/[0.06] text-white/70 border-white/15"}`}>{d.status.replace(/_/g, " ")}</span>
                    <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-white/10 text-white/55">{d.dealType}</span>
                  </div>
                  <p className="text-white/55 text-xs mt-1">{d.companyName} · {inrShort(d.raiseAmountInr)} · min {inrShort(d.minTicketInr)}{d.sector ? ` · ${d.sector}` : ""}</p>
                </div>
                {d.rollup && d.status === "live" && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-white/55"><Eye className="w-3.5 h-3.5 inline mr-1" />{d.rollup.viewed}/{d.rollup.distributed}</span>
                    <span className="text-emerald-400"><Heart className="w-3.5 h-3.5 inline mr-1" />{d.rollup.interested}</span>
                    {d.rollup.committed > 0 && <span className="text-[#FF5C00]"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />{d.rollup.committed}</span>}
                  </div>
                )}
              </div>
              {d.status === "changes_requested" && Array.isArray(d.changesRequested) && d.changesRequested.length > 0 && (
                <div className="mt-2 text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded p-2">
                  Admin requested {d.changesRequested.length} change{d.changesRequested.length === 1 ? "" : "s"}. Click to view and edit.
                </div>
              )}
              {d.status === "rejected" && d.rejectionReason && (
                <div className="mt-2 text-xs text-red-200/90 bg-red-500/10 border border-red-500/25 rounded p-2">
                  <span className="font-mono uppercase tracking-wider mr-2">Rejected:</span>{d.rejectionReason}
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function DealComposer({ existing, onCancel, onSaved }: { existing: any | null; onCancel: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>(existing || {
    title: "", dealType: "primary", instrumentType: "equity", companyName: "", sector: "", geography: "IN",
    raiseAmountInr: "", minTicketInr: "", maxTicketInr: "", preMoneyValInr: "", pricePerShareInr: "",
    summary: "", thesis: "", deckUrl: "", dataRoomUrl: "",
    feeStructureJson: { sourcingPct: 1.5, successPct: 2.0, platformClipPct: 30, currency: "INR", notes: "" },
  });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const numFields = ["raiseAmountInr", "minTicketInr", "maxTicketInr", "preMoneyValInr", "pricePerShareInr"];
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const setFee = (k: string, v: any) => setForm((f: any) => ({ ...f, feeStructureJson: { ...(f.feeStructureJson || {}), [k]: v } }));

  const payload = () => {
    const p: any = { ...form };
    for (const k of numFields) p[k] = p[k] === "" || p[k] == null ? null : Number(p[k]);
    return p;
  };

  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const save = async (): Promise<any | null> => {
    setSaving(true); setErr(null); setSavedFlash(null);
    try {
      const url = existing?.id ? `${BASE}api/banker/curated-deals/${existing.id}` : `${BASE}api/banker/curated-deals`;
      const method = existing?.id ? "PATCH" : "POST";
      const r = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload()) });
      const data = await r.json();
      if (!r.ok) {
        setErr(data.error || "Save failed.");
        setSaving(false);
        // Make sure the user actually sees the error — they may have been
        // scrolled to the bottom of the form when they clicked Save.
        setTimeout(() => document.querySelector("[data-form-error]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 10);
        return null;
      }
      setSaving(false);
      setSavedFlash(`Draft saved at ${new Date().toLocaleTimeString()}`);
      setTimeout(() => setSavedFlash(null), 4000);
      return data;
    } catch (e: any) {
      setErr(e?.message || "Network error.");
      setSaving(false);
      setTimeout(() => document.querySelector("[data-form-error]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 10);
      return null;
    }
  };

  const submitForReview = async () => {
    setSubmitting(true);
    const saved = await save();
    if (!saved) { setSubmitting(false); return; }
    try {
      const r = await fetch(`${BASE}api/banker/curated-deals/${saved.id}/submit`, { method: "POST", headers: headers() });
      if (!r.ok) {
        const e = await r.json();
        setErr(e.error || "Submission failed.");
        setSubmitting(false);
        return;
      }
      onSaved();
    } catch (e: any) { setErr(e?.message || "Network error."); setSubmitting(false); }
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="text-white/55 hover:text-white text-sm flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back to list</button>
        <h2 className="text-xl font-bold text-white">{existing?.id ? "Edit origination" : "New origination"}</h2>
      </div>

      {existing?.changesRequested && Array.isArray(existing.changesRequested) && existing.changesRequested.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 space-y-2">
          <p className="text-amber-200 font-semibold text-sm">Admin requested changes:</p>
          {existing.changesRequested.map((c: any, i: number) => (
            <div key={i} className="text-sm text-amber-200/90"><span className="font-mono text-xs uppercase tracking-wider mr-2 bg-amber-500/20 px-1.5 py-0.5 rounded">{c.field}</span>{c.note}</div>
          ))}
        </div>
      )}

      {err && <div data-form-error className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200 flex items-start gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{err}</span></div>}
      {savedFlash && <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-200 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" />{savedFlash}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Title" required help="A short, recognisable name for the deal. Investors see this in their dashboard and email subject lines. Use the company codename + round, e.g. 'BridgeWave Series-A bridge'. Avoid hype words.">
          <input value={form.title || ""} onChange={e => set("title", e.target.value)} className="input" placeholder="e.g. BridgeWave Series-A bridge" />
        </Field>
        <Field label="Company name" required help="The actual legal / brand name of the company raising. This is shown to investors and used by Kavya in chat. Keep it consistent with the deck.">
          <input value={form.companyName || ""} onChange={e => set("companyName", e.target.value)} className="input" />
        </Field>
        <Field label="Deal type" required help={<><b>Primary</b> = new shares issued by the company; capital goes into the business. <b>Secondary</b> = existing shareholders selling their stake; no money to the company. This drives whether you fill 'Pre-money valuation' or 'Price per share' below.</>}>
          <select value={form.dealType} onChange={e => set("dealType", e.target.value)} className="input">
            <option value="primary">Primary (new issuance)</option>
            <option value="secondary">Secondary (existing-holder sale)</option>
          </select>
        </Field>
        <Field label="Instrument" required help={<><b>Equity</b> = direct shareholding. <b>SAFE</b> = simple agreement for future equity, converts at next priced round. <b>CCPS</b> = compulsorily convertible preference shares (Indian standard for VC rounds). <b>Convertible note</b> = debt that converts to equity. <b>Secondary equity</b> = existing equity changing hands.</>}>
          <select value={form.instrumentType} onChange={e => set("instrumentType", e.target.value)} className="input">
            <option value="equity">Equity</option>
            <option value="safe">SAFE</option>
            <option value="ccps">CCPS</option>
            <option value="convertible_note">Convertible note</option>
            <option value="secondary_equity">Secondary equity</option>
          </select>
        </Field>
        <Field label="Sector" help="Free-text sector or sub-sector (e.g. 'Fintech — lending', 'Climate — battery'). Used for filtering and Drishti analytics.">
          <input value={form.sector || ""} onChange={e => set("sector", e.target.value)} className="input" />
        </Field>
        <Field label="Geography" help="Primary market(s) the company operates in (e.g. 'India', 'India + SEA'). Helpful context — not used for matching in v1.">
          <input value={form.geography || ""} onChange={e => set("geography", e.target.value)} className="input" />
        </Field>
        <Field label="Raise / lot size (INR)" required help="Total amount being raised in this round, or in this allocation if you only have a slice. Enter the full rupee number — e.g. 60000000 for ₹6 crore. The system formats it for display automatically.">
          <input type="number" value={form.raiseAmountInr ?? ""} onChange={e => set("raiseAmountInr", e.target.value)} className="input" placeholder="60000000" />
        </Field>
        <Field label="Min ticket (INR)" required help="Smallest cheque you'll accept from a single investor. Enter the full rupee number — e.g. 2500000 for ₹25 lakh. Investors below this cap won't see a 'commit' option.">
          <input type="number" value={form.minTicketInr ?? ""} onChange={e => set("minTicketInr", e.target.value)} className="input" placeholder="2500000" />
        </Field>
        <Field label="Max ticket (INR)" help="Optional ceiling per investor. Leave blank if there's no cap. Used to keep the cap table from concentrating in any one cheque.">
          <input type="number" value={form.maxTicketInr ?? ""} onChange={e => set("maxTicketInr", e.target.value)} className="input" />
        </Field>
        {form.dealType === "primary" ? (
          <Field label="Pre-money valuation (INR)" required help="Company's value before this round's money goes in. Post-money = pre-money + raise. Investors compute their % using this. Required for primary rounds.">
            <input type="number" value={form.preMoneyValInr ?? ""} onChange={e => set("preMoneyValInr", e.target.value)} className="input" />
          </Field>
        ) : (
          <Field label="Price per share (INR)" required help="The per-share price the seller is asking. For secondary deals there's no 'pre-money' — investors compute their stake from share count and price.">
            <input type="number" value={form.pricePerShareInr ?? ""} onChange={e => set("pricePerShareInr", e.target.value)} className="input" />
          </Field>
        )}
        <Field label="Close by" help="The date you want all commitments in by. Drives Priya's reminder cadence and the 'expiring soon' badge admins see. Leave blank if open-ended.">
          <input type="date" value={form.closeBy ? String(form.closeBy).slice(0, 10) : ""} onChange={e => set("closeBy", e.target.value)} className="input" />
        </Field>
        <Field label="Deck URL" help="Link to the investor pitch deck (Google Drive, Dropbox, Notion, Docsend). Make sure the link permission is set so anyone with the link can view, otherwise investors will hit a wall.">
          <input value={form.deckUrl || ""} onChange={e => set("deckUrl", e.target.value)} className="input" />
        </Field>
        <Field label="Data room URL" help="Link to the full data room (financials, cap table, contracts). Optional — usually shared after first interest. Same access caveat: must be link-viewable, or gated through a tool like Docsend.">
          <input value={form.dataRoomUrl || ""} onChange={e => set("dataRoomUrl", e.target.value)} className="input" />
        </Field>
      </div>

      <Field label={`Investor-facing summary (this is what gets shown — admin can edit before approval) — ${(form.summary || "").length} / 1200`} required help={<>This is the only narrative an investor reads before opening the deck. Cover: <b>what the company does</b>, <b>traction / proof</b>, <b>what the round funds</b>, and <b>why now</b>. 80–1200 chars. Avoid hype phrases — admin will reject submissions with words like 'great opportunity' or 'limited time'. Admin can edit this before approval; once live it's locked.</>}>
        <textarea value={form.summary || ""} onChange={e => set("summary", e.target.value)} maxLength={1200} rows={5} className="input resize-y" placeholder="What the investor sees on the deal page. Admin will lock this once approved." />
      </Field>
      <Field label="Your thesis (private — admin sees, investor doesn't)" help="Your honest internal take: why you sourced this, what's the bull case, what worries you. Admin reads this when approving. Kavya never quotes it to investors. Be candid — this is the place to put the messy stuff that doesn't belong in the public summary.">
        <textarea value={form.thesis || ""} onChange={e => set("thesis", e.target.value)} rows={3} className="input resize-y" />
      </Field>

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-white/70 text-sm font-semibold">Fee structure (required for submission)</p>
          <HelpTip
            title="Fee structure"
            content={<>How the economics of this deal split. All three fields are required. <b>Sourcing %</b> goes to you for bringing the deal. <b>Success %</b> is the carry-style upside on a successful exit. <b>Platform clip %</b> is what Favcy retains. These are stored on the deal record and used by Riya for monthly earnings statements.</>}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Sourcing %" help="Your one-time fee for sourcing the deal, as a % of the total commitment. Typical range 1–3%. Paid out by Favcy after the round closes.">
            <input type="number" step="0.1" value={form.feeStructureJson?.sourcingPct ?? ""} onChange={e => setFee("sourcingPct", Number(e.target.value))} className="input" />
          </Field>
          <Field label="Success %" help="Carry-style fee on the upside at exit, as a % of investor profit. Typical range 10–20%. Paid only if the company exits at a gain.">
            <input type="number" step="0.1" value={form.feeStructureJson?.successPct ?? ""} onChange={e => setFee("successPct", Number(e.target.value))} className="input" />
          </Field>
          <Field label="Platform clip %" help="What Favcy keeps from the round to operate the platform. Set by Favcy policy — confirm the number with admin before submitting.">
            <input type="number" step="1" value={form.feeStructureJson?.platformClipPct ?? ""} onChange={e => setFee("platformClipPct", Number(e.target.value))} className="input" />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10">
        <div className="flex items-center gap-1.5">
          <button onClick={onCancel} className="text-white/55 hover:text-white text-sm">Cancel</button>
          <HelpTip title="Cancel" content="Discards any unsaved edits and returns to your originations list. If you've saved a draft already, the draft stays — only edits since the last save are lost." />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => { const s = await save(); if (s) onSaved(); }} disabled={saving} className="bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save draft
          </button>
          <HelpTip title="Save draft" content="Stores what you've typed so far. Drafts are only visible to you — admins don't see them and they don't go to investors. Come back any time to keep editing." />
          <button onClick={submitForReview} disabled={submitting || saving} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for admin review
          </button>
          <HelpTip title="Submit for admin review" content={<>Sends the draft to admin. They'll either <b>approve</b> (deal goes live to your chosen investors), <b>request changes</b> (you'll see specific notes here), or <b>reject</b> with a reason. Required fields must all be filled and the summary must be at least 80 characters. Once submitted, the draft is locked until admin acts.</>} />
        </div>
      </div>

      <style>{`.input { width: 100%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 8px 12px; font-size: 14px; color: #fff; outline: none; }
.input:focus { border-color: rgba(255,92,0,0.6); }
.input::placeholder { color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}

function Field({ label, required, help, children }: { label: string; required?: boolean; help?: React.ReactNode; children: any }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/55 mb-1">
        <span>{label}{required && <span className="text-[#FF5C00] ml-1">*</span>}</span>
        {help && <HelpTip content={help} title={label.replace(/\s*—.*$/, "")} side="top" />}
      </span>
      {children}
    </label>
  );
}

function DealDetail({ dealId, onBack, onEdit }: { dealId: number; onBack: () => void; onEdit: (d: any) => void }) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [committingFor, setCommittingFor] = useState<number | null>(null);
  const [commitTicket, setCommitTicket] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}api/banker/curated-deals/${dealId}`, { headers: headers() });
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [dealId]);

  const recordCommit = async (investorId: number) => {
    const ticket = Number(commitTicket);
    if (!Number.isFinite(ticket) || ticket <= 0) { alert("Enter a valid commit amount."); return; }
    const r = await fetch(`${BASE}api/banker/curated-deals/${dealId}/commit`, { method: "POST", headers: headers(), body: JSON.stringify({ investorId, ticketInrCommitted: ticket }) });
    if (r.ok) { setCommittingFor(null); setCommitTicket(""); load(); }
    else { const e = await r.json(); alert(e.error || "Commit failed."); }
  };

  if (loading || !data) return <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-8"><Loader2 className="w-6 h-6 animate-spin text-white/55 mx-auto" /></div>;

  const { deal, distributions, earnings } = data;
  const totalEstimate = (earnings || []).filter((e: any) => e.status === "estimate" && e.kind !== "platform_clip_estimate").reduce((s: number, e: any) => s + Number(e.amountInr), 0);
  const totalPending = (earnings || []).filter((e: any) => e.status === "pending" && e.kind !== "platform_clip_estimate").reduce((s: number, e: any) => s + Number(e.amountInr), 0);
  const totalRealized = (earnings || []).filter((e: any) => e.status === "realized" && e.kind !== "platform_clip_estimate").reduce((s: number, e: any) => s + Number(e.amountInr), 0);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="text-white/55 hover:text-white text-sm flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back to originations</button>
        {["draft", "changes_requested"].includes(deal.status) && (
          <button onClick={() => onEdit(deal)} className="bg-white/10 hover:bg-white/15 text-white px-3 py-1.5 rounded-lg text-sm">Edit</button>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-white">{deal.title}</h1>
          <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${STATUS_TONE[deal.status]}`}>{deal.status.replace(/_/g, " ")}</span>
          <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-white/10 text-white/55">{deal.dealType}</span>
        </div>
        <p className="text-white/55 text-sm mt-1">{deal.companyName} · {deal.sector || "—"} · {deal.geography}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Raise" value={inrShort(deal.raiseAmountInr)} />
        <Stat label="Min ticket" value={inrShort(deal.minTicketInr)} />
        <Stat label={deal.dealType === "primary" ? "Pre-money" : "Price/share"} value={inrShort(deal.preMoneyValInr || deal.pricePerShareInr)} />
        <Stat label="Close by" value={deal.closeBy ? new Date(deal.closeBy).toLocaleDateString() : "—"} />
      </div>

      {/* Earnings panel */}
      {deal.feeStructureJson && (
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-white/55 font-mono mb-3 flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> Your earnings</p>
          <div className="grid grid-cols-3 gap-4">
            <div><p className="text-xs text-white/55">Estimate (interest)</p><p className="text-lg font-bold text-white/85 mt-0.5">{inrShort(totalEstimate)}</p></div>
            <div><p className="text-xs text-white/55">Pending (committed)</p><p className="text-lg font-bold text-amber-300 mt-0.5">{inrShort(totalPending)}</p></div>
            <div><p className="text-xs text-white/55">Realized</p><p className="text-lg font-bold text-emerald-300 mt-0.5">{inrShort(totalRealized)}</p></div>
          </div>
          <p className="text-[11px] text-white/40 mt-2">Sourcing {deal.feeStructureJson.sourcingPct}% + Success {deal.feeStructureJson.successPct}% gross, platform clip {deal.feeStructureJson.platformClipPct}%.</p>
        </div>
      )}

      {/* Summary */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
        <p className="text-xs uppercase tracking-wider text-white/55 font-mono mb-2">Investor-facing summary</p>
        <p className="text-sm text-white/85 whitespace-pre-wrap">{deal.summary}</p>
      </div>

      {/* Risk flags */}
      {Array.isArray(deal.riskFlags) && deal.riskFlags.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-white/55 font-mono">Risk flags (admin-attached)</p>
          {deal.riskFlags.map((f: any, i: number) => (
            <div key={i} className={`text-xs px-2 py-1.5 rounded border ${f.severity === "material" ? "bg-red-500/10 border-red-500/30 text-red-200" : f.severity === "caution" ? "bg-amber-500/10 border-amber-500/30 text-amber-200" : "bg-white/[0.04] border-white/10 text-white/70"}`}>
              <span className="font-mono uppercase tracking-wider mr-2">{f.severity}</span>{f.label}
            </div>
          ))}
        </div>
      )}

      {/* Distributions */}
      {distributions.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-white/55 font-mono mb-2 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Distribution ({distributions.length})</p>
          <div className="space-y-1">
            {distributions.map((d: any) => (
              <div key={d.id} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm text-white">{d.investor?.name || `Investor #${d.investorId}`}</p>
                    <p className="text-[11px] text-white/45">{d.investor?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded border border-white/15 text-white/70">{d.state.replace(/_/g, " ")}</span>
                    {d.ticketInrIndicative && <span className="text-xs text-emerald-300">Indicative {inrShort(d.ticketInrIndicative)}</span>}
                    {d.ticketInrCommitted && <span className="text-xs text-[#FF5C00] font-semibold">Committed {inrShort(d.ticketInrCommitted)}</span>}
                  </div>
                </div>
                {["interested", "meeting_requested", "meeting_scheduled"].includes(d.state) && deal.status === "live" && d.ticketInrCommitted == null && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    {committingFor === d.investorId ? (
                      <div className="flex items-center gap-2">
                        <input type="number" value={commitTicket} onChange={e => setCommitTicket(e.target.value)} placeholder="Commit amount (INR)" className="flex-1 bg-white/[0.04] border border-white/15 rounded px-2 py-1 text-sm text-white" />
                        <button onClick={() => recordCommit(d.investorId)} className="text-xs bg-[#FF5C00] hover:bg-[#E55200] text-white px-3 py-1 rounded">Record</button>
                        <button onClick={() => { setCommittingFor(null); setCommitTicket(""); }} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setCommittingFor(d.investorId); setCommitTicket(String(d.ticketInrIndicative || "")); }} className="text-xs text-emerald-300 hover:text-emerald-200">Record commitment →</button>
                    )}
                  </div>
                )}
                {d.noteToAdvisor && <p className="text-xs text-white/55 mt-2 italic">"{d.noteToAdvisor}"</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {deal.status === "live" && distributions.length === 0 && (
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-6 text-center">
          <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
          <p className="text-sm text-white/55">Live but not yet distributed to anyone.</p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{label}</p>
      <p className="text-base font-bold text-white mt-1">{value}</p>
    </div>
  );
}
